// "My majlis" (MAJLIS-67; UI-S3, the approved mock-up's frame 1c): one row per finished majlis this device's identity
// hosts, newest first - the day and start time in the viewer's locale, the length and the seat count, the seats'
// initials in their colours, the languages spoken. No room code (it is stored only hashed, DM-1, and means nothing
// after close, D-16). "Hosted by you" (P-20), each row with "Read links: n" while it has live ones, and - MAJLIS-66,
// P-33 - "Opened here with a read link", shown only when this device has opened one; after a delete, the "Record
// deleted" toast (4b; role=status). NOT built: the mock-up's "Search every record" field above the list - the wire
// ruling (13) has no cross-record route - flagged for the consultant.
import { languageName } from "../data/languages";
import { plural, useI18n } from "../i18n";
import { dayAndTime } from "../i18n/when";
import type { RecordSummary } from "../protocol/records";
import { SEAT_COLOURS } from "../room/model";
import { BackIcon, ChevronEndIcon } from "./icons";
import { PassiveTag } from "./RoomsList";

export function MyMajlis({
  records,
  read = [],
  deletedToast = false,
  failed,
  onOpen,
  onBack,
  onRetry,
}: {
  /** MAJLIS-66: the records this device opened by a read link that is still live */
  read?: RecordSummary[];
  /** MAJLIS-66 (4b): a record was just deleted - the toast shows (the app times it) */
  deletedToast?: boolean;
  records: RecordSummary[] | null;
  failed: boolean;
  onOpen: (roomId: string) => void;
  onBack: () => void;
  onRetry: () => void;
}) {
  const { lang, t } = useI18n();
  const when = (iso: string) => dayAndTime(lang, Date.parse(iso)); // MAJLIS-145: the one day-and-time style (i18n/when)
  return (
    <div className="room records" data-spec="UI-S3 P-20 D-16">
      <header className="room-header record-header">
        <button type="button" className="icon-button" aria-label={t("back")} onClick={onBack}>
          <BackIcon />
        </button>
        <h1 className="records-title">{t("myMajlis")}</h1>
      </header>
      <div className="canvas records-body">
        {/* MAJLIS-66: a phone that only READS by link shows its one section, not an empty "Hosted by you" */}
        {!(records !== null && records.length === 0 && read.length > 0) && (
          <h2 className="records-section">{t("hostedByYou")}</h2>
        )}
        {records === null && !failed && <p className="canvas-empty">{t("recordLoading")}</p>}
        {failed && (
          <div className="canvas-empty record-failed" role="alert">
            <p>{t("recordFailed")}</p>
            <button type="button" className="text-link" onClick={onRetry}>
              {t("tryAgain")}
            </button>
          </div>
        )}
        {records !== null && records.length === 0 && read.length === 0 && (
          <p className="canvas-empty">{t("recordsNone")}</p>
        )}
        {records !== null && records.length > 0 && <Rows list={records} when={when} onOpen={onOpen} />}
        {read.length > 0 && (
          <>
            <h2 className="records-section" data-spec="UI-S3 P-33 DM-12">
              {t("openedHere")}
            </h2>
            <Rows list={read} when={when} onOpen={onOpen} />
          </>
        )}
      </div>
      {deletedToast && (
        <div className="toast toast-note" role="status" data-spec="UI-C14 P-32">
          {t("deleted")}
        </div>
      )}
    </div>
  );
}

function Rows({
  list,
  when,
  onOpen,
}: {
  list: RecordSummary[];
  when: (iso: string) => string;
  onOpen: (roomId: string) => void;
}) {
  const { lang, t } = useI18n();
  return (
    <ul className="records-list">
      {list.map((r) => (
        <li key={r.room_id}>
          <button type="button" className="record-row" data-room={r.room_id} onClick={() => onOpen(r.room_id)}>
            <span className="record-row-main">
              <span className="record-row-when">
                {when(r.started ?? r.created)}
                {r.passive && (
                  <>
                    {" "}
                    <PassiveTag />
                  </>
                )}
              </span>
              <span className="record-row-meta">
                {plural(lang, t, "rowMeta", r.seats.length, { min: r.minutes })}
                {!!r.links && (
                  <>
                    {" · "}
                    <span className="row-links">{t("rowLinks", { n: r.links })}</span>
                  </>
                )}
              </span>
              <span className="record-row-people">
                <span className="record-dots" aria-hidden="true">
                  {r.seats.map((s, i) => (
                    <span key={s.id} className={`record-dot seat-c${i % SEAT_COLOURS}`} lang={s.speak_lang}>
                      {Array.from(s.name.trim())[0] ?? ""}
                    </span>
                  ))}
                </span>
                <span className="record-langs">{r.langs.map((l) => languageName(l, lang)).join(" · ")}</span>
              </span>
            </span>
            <ChevronEndIcon />
          </button>
        </li>
      ))}
    </ul>
  );
}
